import { EventEmitter, Injectable } from "@angular/core";
import { AuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { RouterActions } from "@app/root-store";
import { ApiResultState, LoadingState } from "@app/root-store/api-result.state";
import { Action, select, Store } from "@ngrx/store";
import { filter, map, Observable, switchMap, take, tap } from "rxjs";
import { entityRoutePaths } from "../entity.routes";
import { DealerBranding, DealerBrandingListItem } from "../model/dealer-branding";
import { DealerBrandingActions } from "../store/actions";
import { EntityState } from "../store/entity.state";
import * as DealerBrandingSelectors from "../store/selectors/dealer-branding.selectors";

@Injectable({
    providedIn: "root",
})
export class DealerBrandingService {
    constructor(readonly store: Store<EntityState>) {}

    getEntityKey(id: number) {
        // need to update this once we can save multiple lockout screens
        return `${id}/images/logo.jpg`;
    }

    getAccountKey(id: number) {
        return `account/${id}/images/logo.jpg`;
    }

    /**
     *Dispatches the supplied action
     *
     * @param {Action} action Sends DealerBranding payload or command to Effects and Reducers
     * @returns {void}
     * @memberof DealerBrandingService
     */
    dispatchAction(action: Action): void {
        return this.store.dispatch(action);
    }

    /**
     *Dispatches a save image request action along with image payload
     *
     * @param {DealerBranding} image Image file to be converted to binary string and uploaded to serve
     * @param logData
     * @memberof DealerBrandingService
     */
    saveImage(image: DealerBranding, logData: AuditLogInput = null) {
        this.dispatchAction(DealerBrandingActions.createSet.request({ image, logData }));
    }

    downloadZipFile(key: string, callback: EventEmitter<File>) {
        const zipData = {
            "dealerlockout.png": key,
        };
        this.dispatchAction(new DealerBrandingActions.GetDealerBrandingZipFileDownload({ zipData, callback }));
    }

    getEncodedDealerBrandingZipFile(key: string, callback: EventEmitter<File>) {
        const zipData = {
            "dealerlockout.png": key,
        };
        this.dispatchAction(new DealerBrandingActions.GetEncodedDealerBrandingZipFile({ zipData, callback }));
    }

    getDealerBrandingImageByEntityId(id: number) {
        const key = this.getEntityKey(id);
        return this.loadDealerBrandingImages$(key).pipe(switchMap(_ => this.selectDealerBrandingImageByKey(key)));
    }

    getDealerBrandingImageByKey(key: string) {
        return this.loadDealerBrandingImages$(key).pipe(switchMap(_ => this.selectDealerBrandingImageByKey(key)));
    }

    selectDealerBrandingImageByKey(key: string) {
        return this.store.pipe(
            select(DealerBrandingSelectors.selectDealerBrandingImageByKey(key)),
            filter(image => image !== null)
        );
    }

    getDealerBrandingCallState() {
        return this.store.pipe(select(DealerBrandingSelectors.getDealerBrandingCallState));
    }
    loadDealerBrandingImages$(key: string) {
        return this.store.pipe(
            select(DealerBrandingSelectors.selectDealerBrandingState),
            tap(dealerBrandingState => {
                if (dealerBrandingState.callState === LoadingState.init) {
                    this.store.dispatch(DealerBrandingActions.loadSingle.request({ key }));
                }
            })
        );
    }
    goToDealerBranding(entityId: number) {
        const path = [`/${entityRoutePaths.root}${entityRoutePaths.edit}`, entityId, "dealerbranding"];
        this.dispatchAction(
            RouterActions.routerGo({
                path,
            })
        );
    }
    goToDealerBrandingManagement(entityId: number) {
        this.dispatchAction(
            RouterActions.routerGo({
                path: [`/${entityRoutePaths.root}${entityRoutePaths.edit}`, entityId, "dealerbranding", "create"],
            })
        );
    }

    convertNameToImagePath(name: string, extension: string = "jpg"): string {
        const formattedString = name
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-+)|(-+$)/g, "");

        return `${formattedString}.${extension}`;
    }

    loadDealerBrandingImageList$(entityId: number): Observable<ApiResultState<DealerBrandingListItem[]>> {
        return this.store.pipe(
            select(DealerBrandingSelectors.selectDealerBrandingImagesListState),
            tap(dealerBrandingState => {
                if (dealerBrandingState.callState === LoadingState.init) {
                    this.store.dispatch(DealerBrandingActions.LoadDealerBrandingImageListActions.request({ entityId }));
                }
            }),
            filter(dealerBrandingState => dealerBrandingState.callState === LoadingState.loaded)
        );
    }

    loadDealerBrandingImageByFileName$(entityId: number, fileName: string, key: string): Observable<DealerBranding> {
        return this.store.pipe(
            select(DealerBrandingSelectors.selectDealerBrandingState),
            take(1),
            tap(dealerBrandingState => {
                const imageExists = !!dealerBrandingState.result?.[key];
                const isLoading = dealerBrandingState.callState === LoadingState.loading;
                if (!imageExists && !isLoading) {
                    this.store.dispatch(DealerBrandingActions.LoadDealerBrandingImageActions.request({ entityId, fileName }));
                }
            }),
            switchMap(() =>
                this.store.pipe(
                    select(DealerBrandingSelectors.selectDealerBrandingState),
                    filter(dealerBrandingState => !!dealerBrandingState.result?.[key]),
                    map(dealerBrandingState => dealerBrandingState.result[key]),
                    take(1)
                )
            )
        );
    }

    createDealerBrandingImage(
        entityId: number,
        fileName: string,
        body: Blob,
        callbackEmitter: EventEmitter<any>,
        logData: AuditLogInput = null
    ): void {
        this.store.dispatch(
            DealerBrandingActions.CreateDealerBrandingImageActions.request({ entityId, fileName, body, callbackEmitter, logData })
        );
    }

    deleteDealerBrandingImage(entityId: number, fileName: string, key: string, logData: AuditLogInput = null): void {
        this.store.dispatch(DealerBrandingActions.deleteDealerBrandingImageConfirmation({ entityId, fileName, key, logData }));
    }
}
